import authApi from "interceptor/authInterceptor";
import { createSlice } from "@reduxjs/toolkit";
import { get } from "lodash";
import { dispatch } from "store";
import { onBoardingStatusUpdate, onboardedCompanyDetails } from "./auth";
import { updateSelectedCompany } from "./dashboardSearch";

interface InitialState {
  loader: boolean;
  connections: [];
  tokenData: [];
  users:[];
  comanyData:[]
  deductions: any[];
}

const initialState: InitialState = {
  loader: false,
 connections: [],
 tokenData: [],
 users:[],
 comanyData:[],
 deductions: [],
};

// Helper functions for error handling
const validateApiResponse = (response: any, defaultMsg: string) => {
  const isErrorResponse =
    response?.isAxiosError ||
    response instanceof Error ||
    response?.response?.status >= 400 ||
    response?.response?.data?.code >= 400 ||
    (response?.data && response?.data?.code && response?.data?.code >= 400);

  if (isErrorResponse) {
    const errMsg =
      response?.response?.data?.message ||
      response?.response?.data?.msg ||
      response?.data?.message ||
      response?.message ||
      defaultMsg;
    throw new Error(errMsg);
  }
};

const getErrorMessage = (error: any, defaultMsg: string): string =>
  error?.response?.data?.message ||
  error?.response?.data?.msg ||
  error?.message ||
  defaultMsg;

const getXeroTenantData = createSlice({
  name: "getXeroTenantData",
  initialState,
  reducers: {
    getTenantData(state, action) {
      let { connections, tokenData, comanyData } = action.payload;
      state.connections = connections || initialState.connections;
      state.tokenData = tokenData || initialState.tokenData;
      state.comanyData= comanyData || initialState.comanyData;
    },
    getUserData(state, action){
      let {users, deductions} = action.payload;
      state.users = users || initialState.users;
      state.deductions = deductions || [];
    },
    isLoading(state, action) {
      state.loader = action.payload;
    },
  },
});

export const { getTenantData, getUserData,isLoading } =
  getXeroTenantData.actions;

export default getXeroTenantData.reducer;

export function processXeroAccessToken(code: string, isReauth?: boolean, companyId?: string) {
  return async () => {
    try {
      const response: any = await authApi.post(`/v2/api/xero/getAccessToken`, {
        code,
        isReauth,
        companyId,
      });

      validateApiResponse(response, "Something went wrong, please try again");

      if(response.data){
        const xeroData = response.data.data.response;
        dispatch(getTenantData({
          connections: get(xeroData,"connections",""),
          tokenData : get(xeroData, "tokenData",""),
          comanyData: get(xeroData,"companyResponses","")
      }))
      }

      if (!response?.data?.data)
        throw new Error("Something went wrong, please try again");

      return response?.data?.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, "Something went wrong, please try again"));
    }
  };
}

export function getEmployeeInforXero(payload: any){
  return async()=>{
    try {
      const response: any = await authApi.post(`/v2/api/xero/getUserInfor`, payload);
      console.log(response?.data || response, "API Response");

      validateApiResponse(response, "Failed to fetch Xero employee info");

      if(response.data){
        const userData = response.data.data;
        dispatch(getUserData({
          users: get(userData, "cashdUser", []),
          deductions: get(userData, "deductions", [])
        }))
      }

      return response;
    } catch (error: any) {
      console.error("API Error:", error);
      throw new Error(getErrorMessage(error, "Failed to fetch Xero employee info"));
    }
  }
}

export function saveXeroDetails(payload: any){
 return async()=>{
  try {
    const response: any = await authApi.post(`/v2/api/xero/saveDetails`, payload);
    console.log(response?.data || response, "API Response");

    validateApiResponse(response, "Failed to save Xero details");

    const companyData = response?.data?.data?.company;

    // dispatch event to update company details
    dispatch(onBoardingStatusUpdate({ onBoardedCompanyData: companyData }));

    const onBoardedUserCompanyDetails = {
      company_name: get(companyData, "company_name", ""),
      _id: get(companyData, "_id", ""),
    };

    dispatch(
      onboardedCompanyDetails({
        onBoardedCompanyDetails: onBoardedUserCompanyDetails,
      })
    );

    dispatch(
      updateSelectedCompany({ selectedCompany: onBoardedUserCompanyDetails })
    );
  } catch (error: any) {
    console.error("API Error:", error);
    throw new Error(getErrorMessage(error, "Failed to save Xero details"));
  }
 }
}

export function fetchXeroDeductionItems(companyId: string) {
  return async () => {
    const response: any = await authApi.post(`/v2/api/xero/fetchXeroDeductionItems`, { companyId });
    validateApiResponse(response, "Failed to fetch Xero deduction items.");
    return response?.data?.data;
  };
}

export function updateXeroDeductionMappings(companyId: string, payload: any) {
  return async () => {
    const response: any = await authApi.post(`/v2/api/xero/updateDeductionMappings`, {
      companyId,
      ...payload,
    });
    validateApiResponse(response, "Failed to update Xero deduction mappings.");
    return response?.data;
  };
}

